// @ts-nocheck
// @ts-nocheck
import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Chart as ChartJS, registerables } from "chart.js";
import { getRelativePosition } from "chart.js/helpers";
import annotationPlugin from "chartjs-plugin-annotation";
import { Line } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import { dismissTooltipPlugin, verticalLineIntersect } from "./helpers/GraphPlugins";
import useIsMobile from "../../hooks/useIsMobile";
import GraphDesktopControls from "./helpers/GraphDesktopControls";
import GraphMobileTimeLabel from "./helpers/GraphMobileTimeLabel";
import GraphModals from "./helpers/GraphModals";
import useGraphControls from "./helpers/useGraphControls";

import {
  applyMobileTicks,
  buildXAxisTicks,
  formatYAxisTick,
  buildZoomOptions,
  createMonthDayTickCallback,
  TIME_RANGES,
} from "./graphutil";
import { buildTooltipOptions, formatDateTimeLabel } from "./forecastUtil";
import { niceAxisBounds, roundTicks } from "../../utils/chartValueAxis";

// This contains code for processed Models
ChartJS.register(...registerables, annotationPlugin, zoomPlugin);


const hexColors = [
  "#FF5733",
  "#16537e", //def keep
  "#3357FF",
  "#FF33A1",
  "#A133FF",
  "#33FFF1",
  "#FF5733",
  "#FFC300",
  "#DAF7A6",
  "#C70039",
];

const preSetChannelColors = {
  mean: "#EF5350",
  Max: "#4B0082", // Dark purple for Max channel
  Min: "#DDA0DD", // Light purple for Min channel
  "Current Year": "#000000", // Black for Current Year channel
  // Year gradient from light blue (oldest) to green to yellow to red (newest) - newer years are more defined
  Y1982: "#E3F2FD", // Very light blue - oldest
  Y1983: "#BBDEFB", // Light blue
  Y1984: "#90CAF9", // Light blue
  Y1985: "#81D4FA", // Light blue
  Y1986: "#4FC3F7", // Medium-light blue
  Y1987: "#29B6F6", // Light blue
  Y1988: "#03A9F4", // Medium-light blue
  Y1989: "#00ACC1", // Light cyan
  Y1990: "#00BCD4", // Light cyan
  Y1991: "#26C6DA", // Light cyan
  Y1992: "#4DD0E1", // Light cyan
  Y1993: "#4DB6AC", // Light teal
  Y1994: "#26A69A", // Medium teal
  Y1995: "#00897B", // Teal
  Y1996: "#26A69A", // Medium teal-green
  Y1997: "#4DB6AC", // Light teal-green
  Y1998: "#66BB6A", // Light green
  Y1999: "#81C784", // Light green
  Y2000: "#9CCC65", // Light green-yellow
  Y2001: "#AED581", // Light green-yellow
  Y2002: "#C5E1A5", // Pale green-yellow
  Y2003: "#D4E157", // Light yellow-green
  Y2004: "#CDDC39", // Yellow-green
  Y2005: "#DCE775", // Light yellow
  Y2006: "#E6EE9C", // Very light yellow
  Y2007: "#F0F4C3", // Pale yellow
  Y2008: "#FDD835", // Bright yellow
  Y2009: "#FBC02D", // Yellow
  Y2010: "#F9A825", // Yellow-orange
  Y2011: "#F57C00", // Orange
  Y2012: "#FF6F00", // Bright orange
  Y2013: "#E65100", // Dark orange
  Y2014: "#FF5722", // Deep orange-red
  Y2015: "#E64A19", // Orange-red
  Y2016: "#D84315", // Red-orange
  Y2017: "#BF360C", // Deep red-orange
  Y2018: "#C62828", // Red
  Y2019: "#B71C1C", // Dark red
  Y2020: "#C62828", // Red
  Y2021: "#B71C1C", // Dark red
  Y2022: "#A02800", // Very dark red
  Y2023: "#8B0000", // Dark red
  Y2024: "#6B0000", // Deepest red - most defined

  "WY 1982/1983": "#E3F2FD", // Very light blue - oldest
  "WY 1983/1984": "#BBDEFB", // Light blue
  "WY 1984/1985": "#90CAF9", // Light blue
  "WY 1985/1986": "#81D4FA", // Light blue
  "WY 1986/1987": "#4FC3F7", // Medium-light blue
  "WY 1987/1988": "#29B6F6", // Light blue
  "WY 1988/1989": "#03A9F4", // Medium-light blue
  "WY 1989/1990": "#00ACC1", // Light cyan
  "WY 1990/1991": "#00BCD4", // Light cyan
  "WY 1991/1992": "#26C6DA", // Light cyan
  "WY 1992/1993": "#4DD0E1", // Light cyan
  "WY 1993/1994": "#4DB6AC", // Light teal
  "WY 1994/1995": "#26A69A", // Medium teal
  "WY 1995/1996": "#00897B", // Teal
  "WY 1996/1997": "#26A69A", // Medium teal-green
  "WY 1997/1998": "#4DB6AC", // Light teal-green
  "WY 1998/1999": "#66BB6A", // Light green
  "WY 1999/2000": "#81C784", // Light green
  "WY 2000/2001": "#9CCC65", // Light green-yellow
  "WY 2001/2002": "#AED581", // Light green-yellow
  "WY 2002/2003": "#C5E1A5", // Pale green-yellow
  "WY 2003/2004": "#D4E157", // Light yellow-green
  "WY 2004/2005": "#CDDC39", // Yellow-green
  "WY 2005/2006": "#DCE775", // Light yellow
  "WY 2006/2007": "#E6EE9C", // Very light yellow
  "WY 2007/2008": "#F0F4C3", // Pale yellow
  "WY 2008/2009": "#FDD835", // Bright yellow
  "WY 2009/2010": "#FBC02D", // Yellow
  "WY 2010/2011": "#F9A825", // Yellow-orange
  "WY 2011/2012": "#F57C00", // Orange
  "WY 2012/2013": "#FF6F00", // Bright orange
  "WY 2013/2014": "#E65100", // Dark orange
  "WY 2014/2015": "#FF5722", // Deep orange-red
  "WY 2015/2016": "#E64A19", // Orange-red
  "WY 2016/2017": "#D84315", // Red-orange
  "WY 2017/2018": "#BF360C", // Deep red-orange
  "WY 2018/2019": "#C62828", // Red
  "WY 2019/2020": "#B71C1C", // Dark red
  "WY 2020/2021": "#C62828", // Red
  "WY 2021/2022": "#B71C1C", // Dark red
  "WY 2022/2023": "#A02800", // Very dark red
  "WY 2023/2024": "#8B0000", // Dark red
  "WY 2024/2025": "#6B0000", // Deepest red - most defined
};

// Craig Adjust these
const percentileColors = {
  3: ["#e0e6c5", "#8ba043", "#e0e6c5"],
  5: ["#cfd5b3", "#abb57b", "#5e692e", "#abb57b", "#cfd5b3"],
  7: [
    "#e0e6c5",
    "#c2d08f",
    "#a5b962",
    "#8ba043",
    "#a5b962",
    "#c2d08f",
    "#e0e6c5",
  ],
  9: [
    "#e0e6c5",
    "#c2d08f",
    "#a5b962",
    "#8ba043",
    "#71882a",
    "#8ba043",
    "#a5b962",
    "#c2d08f",
    "#e0e6c5",
  ],
};

// Percentile counts without a preset palette (e.g. a lone p5/p95 pair): shade by distance from the centre,
// innermost darkest, using the 9-band palette's outer→inner half.
const percentileColor = (count, index) => {
  if (percentileColors[count]) return percentileColors[count][index];
  const shades = percentileColors[9].slice(0, 5);
  const innermost = Math.floor((count - 1) / 2);
  const depth = Math.min(index, count - 1 - index);
  return shades[Math.max(0, shades.length - 1 - (innermost - depth))];
};

const areNumbersSwitched = (str1, str2) => {
  // Extract the first 5 characters and split by "-"
  const [num1a, num1b] = str1.substring(0, 5).split("-");
  const [num2a, num2b] = str2.substring(0, 5).split("-");

  // Compare as numbers: a single-digit band ("5-95 Percentile") keeps a trailing space in its first five characters
  return Number(num1a) === Number(num2b) && Number(num1b) === Number(num2a);
};

// Chart.js draws the lowest `order` on top. A Custom View passes each source's list position as its layer (0 = top
// row), so the list order is the draw order; without layers the overlays sit above the model as before.
const layerOrder = (layer, offset = 0) => -1000 + layer * 10 + offset;
const hasLayer = (layer) => typeof layer === "number" && Number.isFinite(layer);

const FALLBACK_SOURCE_COLORS = [
  "#2563eb",
  "#16a34a",
  "#dc2626",
  "#f97316",
  "#7c3aed",
  "#0f766e",
  "#9333ea",
  "#d97706",
];

const ProcessedScatterGraph = React.memo(
  ({
    percentiles,
    otherChannels,
    labels,
    yaxis,
    timeZone,
    assetTimeZone,
    timeZoneMode,
    lineTension = 0.2,
    title = "Processed Model",
    onTimeZoneChange,
    additionalDataSources = null,
    onRegisterControls,
    desktopExtraControls,
    hideGraphControls = false,
    hideMobileControls = false,
    desktopLeftContent = null,
    mobileControls = null,
    // Optional overrides for a caller with its own palette (Custom Views): meanColor paints the main line (mean, else the first channel) and meanLabel names it; omitted, the graph keeps its usual colours, labels and red now line
    meanColor = null,
    meanLabel = null,
    nowLineColor = null,
    // Colour overrides (composed-forecast display settings): by channel ("mean", "control", "p50" …) and by band
    // ("p25-p75", colouring both edges and the shading). Unset channels keep the defaults below.
    channelColors = null,
    bandColors = null,
    // The model's place in a Custom View's source list (see layerOrder); unset keeps the model under the overlays
    baseLayer = null,
  }) => {

    const isMobile = useIsMobile();
    const chartRef = React.useRef(null);
    const currentViewRef = React.useRef({
      start: new Date(),
      end: new Date()
    });
    const [isDatasetModalOpen, setIsDatasetModalOpen] = useState(false);
    const [isTimeRangeModalOpen, setIsTimeRangeModalOpen] = useState(false);
    const [visibleDatasets, setVisibleDatasets] = useState({});
    const [selectedTimeRange, setSelectedTimeRange] = useState('full');
    const [isZoomed, setIsZoomed] = useState(false);

    useGraphControls({
      onRegisterControls,
      setIsTimeRangeModalOpen,
      setIsDatasetModalOpen,
      currentViewRef,
      selectedTimeRange,
    });

    const handleResetZoom = () => {
      if (chartRef.current) {
        chartRef.current.resetZoom();
        setIsZoomed(false);
      }
    };

    // Add double-click to reset zoom
    useEffect(() => {
      let canvas: HTMLCanvasElement | null = null;
      const handleDoubleClick = () => {
        handleResetZoom();
      };

      // Use a small delay to ensure chart is fully initialized
      const timer = setTimeout(() => {
        const chart = chartRef.current;
        if (!chart || !chart.canvas) return;
        canvas = chart.canvas;
        canvas.addEventListener('dblclick', handleDoubleClick);
      }, 100);

      return () => {
        clearTimeout(timer);
        if (canvas) {
          canvas.removeEventListener('dblclick', handleDoubleClick);
        }
      };
    }, []);

    // Log additional data sources when they're available
    useEffect(() => {
      if (additionalDataSources && additionalDataSources.length > 0) {
        additionalDataSources.forEach((source, index) => {
          console.log(`Data Source [${index}]:`, {
            name: source.name,
            parameterId: source.parameterId,
            source: source.source,
            schema: source.schema,
            lookbackDays: source.lookbackDays,
            color: source.color,
            recordCount: source.records?.length || 0,
            sampleRecord: source.records?.[0]
          });
        });
      }
    }, [additionalDataSources]);
    
    const additionalDataPointSets = useMemo(() => {
      if (!Array.isArray(additionalDataSources)) {
        return [];
      }

      return additionalDataSources
        .map((source, index) => {
          const records = Array.isArray(source.records) ? source.records : [];
          const dataPoints = records
            .filter(
              (record) =>
                record &&
                record.timestamp &&
                record.value !== null &&
                record.value !== undefined
            )
            .map((record) => ({
              x: new Date(record.timestamp),
              y: Number(record.value),
            }))
            .sort((a, b) => a.x.getTime() - b.x.getTime());

          if (!dataPoints.length) {
            return null;
          }

          const baseColor =
            source.color ||
            FALLBACK_SOURCE_COLORS[index % FALLBACK_SOURCE_COLORS.length];

          return {
            label: source.name || `Data Source ${index + 1}`,
            type: "line",
            data: dataPoints,
            borderColor: baseColor,
            backgroundColor: baseColor,
            borderWidth: 3,
            radius: 0,
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.2,
            fill: false,
            spanGaps: true,
            order: hasLayer(source.layer) ? layerOrder(source.layer) : -300,
            clip: false,
            _sourceName: source.name || `Data Source ${index + 1}`,
            // axis: "right" plots against the secondary y1 scale (e.g. a forecast error series)
            ...(source.axis === "right"
              ? { yAxisID: "y1", borderWidth: 2, borderDash: source.borderDash ?? [4, 3], _rightAxis: true }
              : {}),
          };
        })
        .filter(Boolean);
    }, [additionalDataSources]);
    
    const rightAxis = useMemo(() => {
      const source = Array.isArray(additionalDataSources)
        ? additionalDataSources.find((src) => src?.axis === "right")
        : null;
      return source ? { label: source.axisLabel || source.name || "", sharedScale: !!source.sharedScale } : null;
    }, [additionalDataSources]);

    // Calculate dynamic y-axis min and max values with appropriate padding
    const { minYValue, maxYValue, yStep } = useMemo(() => {
      const baseValues: number[] = [
        ...Object.values(percentiles),
        ...Object.values(otherChannels),
      ]
        .flat()
        .map((item) => item?.y)
        .filter((val) => val !== undefined && val !== null)
        .map((val) => Number(val));

      // A right-axis series sharing the left scale (e.g. an error in MW) must fit inside it too
      const overlayValues = additionalDataPointSets
        .filter((dataset) => !dataset._rightAxis || rightAxis?.sharedScale)
        .flatMap((dataset) => dataset.data)
        .map((point) => point?.y)
        .filter((val) => val !== undefined && val !== null)
        .map((val) => Number(val));

      const allValues = [...baseValues, ...overlayValues];
      
      // If no valid data points, return defaults
      if (allValues.length === 0) {
        return { minYValue: 0, maxYValue: 100, yStep: 20, yAxisPadding: { top: 10, bottom: 10 } };
      }
      
      const min = Math.min(...allValues);
      const max = Math.max(...allValues);
      
      // Calculate the data range
      const range = max - min;
      
      // Apply different padding strategies based on data range
      let topPaddingPercent, bottomPaddingPercent;
      
      if (range < 0.001) {
        // For extremely small ranges (nearly flat line), add significant padding
        topPaddingPercent = 0.5; // 50% padding
        bottomPaddingPercent = 0.5; // 50% padding
      } else if (range < 0.1) {
        // For very small ranges, add more padding
        topPaddingPercent = 0.2; // 20% padding
        bottomPaddingPercent = 0.2; // 20% padding
      } else if (range < 1) {
        // For small ranges
        topPaddingPercent = 0.15; // 15% padding
        bottomPaddingPercent = 0.15; // 15% padding
      } else if (range < 10) {
        // For medium ranges
        topPaddingPercent = 0.1; // 10% padding
        bottomPaddingPercent = 0.1; // 10% padding
      } else {
        // For large ranges
        topPaddingPercent = 0.08; // 8% padding
        bottomPaddingPercent = 0.08; // 8% padding
      }
      
      // Calculate the padded min and max
      // Data that never dips below zero (power, flow) keeps a zero floor instead of padding to a meaningless negative
      const paddedMin = min >= 0 ? Math.max(0, min - range * bottomPaddingPercent) : min - range * bottomPaddingPercent;
      const paddedMax = max + (range * topPaddingPercent);
      // Widened to round steps, so the axis starts and ends on a tick like the others (not 126 → 384)
      const nice = niceAxisBounds(paddedMin, paddedMax);

      return {
        minYValue: nice.min,
        maxYValue: nice.max,
        yStep: nice.step,
        yAxisPadding: { 
          top: topPaddingPercent * 100, 
          bottom: bottomPaddingPercent * 100 
        }
      };
    }, [percentiles, otherChannels, additionalDataPointSets, rightAxis]);

    const maxXValue = [
      ...Object.values(percentiles),
      ...Object.values(otherChannels),
      ...additionalDataPointSets.map((dataset) => dataset.data),
    ]
      .flat()
      .reduce((max, item) => Math.max(max, new Date(item?.x).getTime()), 0);

    const minXValue = [
      ...Object.values(percentiles),
      ...Object.values(otherChannels),
      ...additionalDataPointSets.map((dataset) => dataset.data),
    ]
      .flat()
      .reduce(
        (min, item) => Math.min(min, new Date(item?.x).getTime()),
        Infinity
      );
      
    // Calculate time window based on the current time and data
    const getTimeWindow = (hoursToShow) => {
      // Combine all data points for finding closest to now
      const allPointsArrays = [
        ...Object.values(percentiles),
        ...Object.values(otherChannels),
        ...additionalDataPointSets.map((dataset) => dataset.data),
      ];
      
      if (!allPointsArrays.length) return { start: new Date(), end: new Date() };
      
      // Get all time points from the data
      const allTimePoints = allPointsArrays
        .flat()
        .map(item => new Date(item.x).getTime())
        .sort((a, b) => a - b);
      
      // Find the closest point to current time
      const nowTime = new Date().getTime();
      let closestPointIndex = 0;
      let minDiff = Infinity;
      
      allTimePoints.forEach((time, index) => {
        const diff = Math.abs(time - nowTime);
        if (diff < minDiff) {
          minDiff = diff;
          closestPointIndex = index;
        }
      });
      
      // If we have a full range request, just return min/max of all data
      if (hoursToShow === 'full') {
        return {
          start: new Date(minXValue),
          end: new Date(maxXValue)
        };
      }
      
      // Otherwise, use the hours parameter to define the window
      const startTime = allTimePoints[Math.max(0, closestPointIndex)];
      const endTime = startTime + (parseInt(hoursToShow) * 60 * 60 * 1000);
      
      return {
        start: new Date(startTime),
        end: new Date(endTime)
      };
    };
    
    // Memoize the initial time window based on device
    const initialTimeWindow = useMemo(
      () => getTimeWindow('full'),
      [percentiles, otherChannels, additionalDataPointSets]
    );
    
    // Apply time range to chart
    const applyTimeRange = (rangeValue) => {
      if (!chartRef.current) return;
      
      const timeWindow = getTimeWindow(rangeValue);
      
      chartRef.current.zoomScale('x', {
        min: timeWindow.start.getTime(),
        max: timeWindow.end.getTime()
      });
      
      // Update the current view reference
      currentViewRef.current = timeWindow;
      
      // Update the selected time range
      setSelectedTimeRange(rangeValue);
    };

    const handleToggleVisibility = (e, legendItem) => {
      const index = legendItem.datasetIndex;
      const ci = e.chart;
      const meta = ci.getDatasetMeta(index);

      // Toggle the visibility
      meta.hidden = !meta.hidden;

      // Also toggle other datasets with the same group label
      ci.data.datasets.forEach((dataset, i) => {
        if (areNumbersSwitched(dataset.label, legendItem.text)) {
          ci.getDatasetMeta(i).hidden = meta.hidden;
        }
      });

      ci.update();
    };

    const rafRef = React.useRef(null);

    const handleTouchMove = useCallback(
      (event) => {
        if (!isMobile || !chartRef.current) return;
        const nativeEvent = event.nativeEvent;
        if (!nativeEvent) return;
        const chart = chartRef.current;
        const position = getRelativePosition(nativeEvent, chart);

        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
        rafRef.current = requestAnimationFrame(() => {
          const synthetic = {
            type: "mousemove",
            chart,
            native: nativeEvent,
            x: position.x,
            y: position.y,
          };
          const points = chart.getElementsAtEventForMode(
            synthetic,
            "nearest",
            { intersect: false, axis: "x" },
            true
          );
          if (points.length) {
            chart.tooltip.setActiveElements(points, { x: position.x, y: position.y });
            chart.update();
          }
        });
      },
      [isMobile]
    );

    // At the full range the bounds' own step, so both ends are ticks; zoomed, a fresh step for what is shown
    const axisStep = (axis) => (axis.min === minYValue && axis.max === maxYValue ? yStep : undefined);

    const options = useMemo(
      () => ({
        tension: lineTension,
        interaction: {
          mode: "nearest",
          intersect: false,
          axis: "x",
        },
        events: ["mousemove", "mouseout", "click", "touchstart", "touchmove", "touchend"],
        plugins: {
          zoom: {
            ...buildZoomOptions({ 
              isMobile, 
              minXValue, 
              maxXValue, 
              minYValue, 
              maxYValue, 
              currentViewRef,
              onZoomReset: () => setIsZoomed(false)
            }),
            onZoomComplete: () => setIsZoomed(true),
            onZoomReset: () => setIsZoomed(false),
          },
          tooltip: buildTooltipOptions({
            timeZone,
            labelFormatter: function (context) {
              const datasetLabel =
                context.dataset._sourceName ||
                context.dataset.label ||
                "";
              const value = context.parsed.y.toFixed(1);

              const regex = /^(\d+)-\d+ Percentile$/;
              const match = datasetLabel ? datasetLabel.match(regex) : null;

              if (match) {
                return `${match[1]} Percentile: ${value}`;
              }

              return datasetLabel
                ? `${datasetLabel}: ${value}`
                : `${value}`;
            },
          }),

          verticalLine: {},
          annotation: {
            annotations: {
              currentUTCTime: {
                type: "line",
                xMin: new Date().toISOString(),
                xMax: new Date().toISOString(),
                borderColor: nowLineColor || "rgb(255, 99, 132)",
                borderWidth: nowLineColor ? 1.5 : 2,
                borderDash: nowLineColor ? [4, 4] : undefined,
                label: {
                  display: !isMobile,
                  content: () => {
                    const newTimeDate = new Date();
                    return formatDateTimeLabel(newTimeDate, timeZone);
                  },
                  enabled: true,
                  position: "end",
                },
              },
            },
          },
          legend: {
            display: false, // Hide legend on all screens
            position: 'top',
            onClick: handleToggleVisibility,
            labels: {
              filter: function (legendItem, chartData) {
                // Hide 'Dataset 2' from the legend
                const firstFive = legendItem.text.substring(0, 5);
                const percentileFlag = firstFive.match(/(\d+)-(\d+)/);
                if (!percentileFlag) {
                  return true;
                }
                const [first, second] = firstFive.split("-");

                return second >= first;
              },
            },
          },
        },
        maintainAspectRatio: false,
        spanGaps: true,
        scales: {
          x: {
            stacked: true,
            display: true,
            type: "time",
            // Apply time range based on device type
            min: initialTimeWindow.start.getTime(),
            max: initialTimeWindow.end.getTime(),
            offset: false,
            grid: {
              ...(isMobile ? { drawBorder: true, drawTicks: false } : {}),
            },
            title: {
              display: true,
            },
            ticks: applyMobileTicks(
              buildXAxisTicks(createMonthDayTickCallback(), isMobile),
              isMobile,
              { maxTicksLimit: 6, autoSkipPadding: 24 } // Padding covers the edge labels' inner shift, so autoSkip drops a date instead of letting two touch
            ),
          },
          y: {
            display: true,
            stacked: false,
            title: {
              display: !isMobile && !!yaxis,
              text: yaxis,
              font: { size: 13, weight: "500" },
            },
            // Round bounds (niceAxisBounds) with a matching step, so every tick — the ends included — is evenly spaced
            min: minYValue,
            max: maxYValue,
            // Ticks on round steps of whatever range is shown, so a zoom gets fresh ticks rather than odd end labels
            afterBuildTicks: (axis) => {
              axis.ticks = roundTicks(axis.min, axis.max, axisStep(axis));
            },
            ticks: applyMobileTicks(
              {
                padding: 2,
                display: true,
                callback: formatYAxisTick,
              },
              isMobile,
              { mirror: false, padding: 4, font: { size: 8 } }
            ),
            grid: {
              ...(isMobile
                ? { drawBorder: false } // MIKE ADD remove left border line on graph for mobile
                : {}),
            },
            border: isMobile ? { display: false } : undefined, // MIKE ADD remove left border line on graph for mobile
          },
          // Secondary axis for overlays flagged axis: "right" (forecast error); titled with the metric
          ...(rightAxis
            ? {
                y1: {
                  display: true,
                  position: "right",
                  grid: { drawOnChartArea: false },
                  title: { display: !isMobile, text: rightAxis.label },
                  ...(rightAxis.sharedScale
                    ? { min: minYValue, max: maxYValue, afterBuildTicks: (axis) => { axis.ticks = roundTicks(axis.min, axis.max, axisStep(axis)); } }
                    : {}),
                  ticks: applyMobileTicks(
                    {
                      padding: 2,
                      // Shared scale: label ticks exactly like the left axis
                      ...(rightAxis.sharedScale
                        ? { callback: (value) => (value === null || value === undefined ? value : Math.round(Number(value))) }
                        : {}),
                    },
                    isMobile,
                    { padding: 4, font: { size: 8 } }
                  ),
                },
              }
            : {}),
        },
        layout: { // MIKE ADD. shift graph to right on mobile to make space for y axis labels. can be adjusted further if needed
          padding: {
            left: isMobile ? 1 : 0,
          },
        },
      }),
      [minYValue, maxYValue, yStep, yaxis, lineTension, timeZone, isMobile, initialTimeWindow, minXValue, maxXValue, nowLineColor, rightAxis]
    );

    const boxplotData = useMemo(() => {
      const datasets = [
        ...additionalDataPointSets,
        ...Object.keys(otherChannels).map((channel, jindex) => {
          let color;
          // A display-settings colour the user chose wins; else a Custom View's palette colours its main line
          const isMainLine = "mean" in otherChannels ? channel === "mean" : jindex === 0;
          if (channelColors?.[channel]) {
            color = channelColors[channel];
          } else if (meanColor && isMainLine) {
            color = meanColor;
          } else if (Object.keys(preSetChannelColors).includes(channel)) {
            color = preSetChannelColors[channel];
          } else {
            color = hexColors[jindex]; //default
          }

          return {
            label: meanLabel && isMainLine ? meanLabel : channel.charAt(0).toUpperCase() + channel.slice(1),
            type: "line",
            radius: 0,
            backgroundColor: color,
            borderColor: color,
            data: otherChannels[channel],
            order: hasLayer(baseLayer) ? layerOrder(baseLayer, 1) : -200,
          };
        }),

        ...Object.keys(percentiles).map((key, index) => {
          const channelCount = Object.keys(percentiles).length;
          const percentile = parseInt(key.replace("p", ""));
          const oppositePercentile = 100 - percentile;
          let label = `${percentile}-${oppositePercentile} Percentile`;
          if (percentile === oppositePercentile) {
            label = `${percentile} Percentile`;
          }
          const lowEdge = Math.min(percentile, oppositePercentile);
          const color =
            channelColors?.[key] ||
            (percentile !== 50 ? bandColors?.[`p${lowEdge}-p${100 - lowEdge}`] : null) ||
            percentileColor(channelCount, index);
          return {
            label,
            type: "line",
            data: percentiles[key],
            radius: 0,
            backgroundColor: color,
            borderColor: color,
            borderWidth: 3.5,
            order: hasLayer(baseLayer) ? layerOrder(baseLayer, 2) : -150,

            fill:
              Math.floor(channelCount / 2) === index
                ? false
                : index < Math.floor(channelCount / 2)
                ? `+1`
                : "-1", // Fill down to the next dataset
          };
        }),
      ];
      return { datasets };
    }, [percentiles, otherChannels, additionalDataPointSets, meanColor, meanLabel, channelColors, bandColors, baseLayer]);

    // Apply initial time range on component mount
    useEffect(() => {
      if (chartRef.current && (percentiles || otherChannels || additionalDataPointSets.length)) {
        // Set the initial time window
        const timeWindow = getTimeWindow(selectedTimeRange);
        
        chartRef.current.zoomScale('x', {
          min: timeWindow.start.getTime(),
          max: timeWindow.end.getTime()
        });
        
        // Store the initial view reference
        currentViewRef.current = timeWindow;
      }
    }, [percentiles, otherChannels, additionalDataPointSets, selectedTimeRange]);
    
    // Initialize visibleDatasets
    useEffect(() => {
      if (boxplotData && boxplotData.datasets) {
        const initialVisibility = {};
        boxplotData.datasets.forEach((dataset, index) => {
          initialVisibility[index] = true;
        });
        setVisibleDatasets(initialVisibility);
      }
    }, [boxplotData]);
    
    // Keep processed models defaulting to full range on all devices
    
    if (!percentiles && !otherChannels) {
      return false;
    }
    
    // Toggle dataset visibility
    const toggleDatasetVisibility = (index) => {
      if (chartRef.current) {
        const chart = chartRef.current;
        const meta = chart.getDatasetMeta(index);
        
        // Toggle the visibility
        meta.hidden = !meta.hidden;
        
        // Also toggle other datasets with the same group label
        boxplotData.datasets.forEach((dataset, i) => {
          if (dataset.label && boxplotData.datasets[index].label && 
              areNumbersSwitched(dataset.label, boxplotData.datasets[index].label)) {
            chart.getDatasetMeta(i).hidden = meta.hidden;
            setVisibleDatasets(prev => ({...prev, [i]: !meta.hidden}));
          }
        });
        
        setVisibleDatasets(prev => ({...prev, [index]: !meta.hidden}));
        chart.update();
      }
    };
    
    // Prepare datasets for the modal
    const datasetItems = boxplotData.datasets.map((dataset, index) => {
      // Skip percentile datasets with reversed numbers
      const firstFive = dataset.label?.substring(0, 5) || '';
      const percentileFlag = firstFive.match(/(\d+)-(\d+)/);
      if (percentileFlag) {
        const [first, second] = firstFive.split("-");
        if (Number(second) < Number(first)) return null;
      }
        
      return {
        id: index.toString(),
        label: dataset.label,
        visible: visibleDatasets[index] !== false,
        color: dataset.borderColor || dataset.backgroundColor
      };
    }).filter(Boolean);
    
    if (percentiles || otherChannels) {
      return (
        <div className="flex flex-col w-full h-full">
          {!hideGraphControls && (
            <GraphDesktopControls
              selectedTimeRange={selectedTimeRange}
              timeRanges={TIME_RANGES}
              onOpenTimeRange={() => setIsTimeRangeModalOpen(true)}
              onOpenDataset={() => setIsDatasetModalOpen(true)}
              extraControls={desktopExtraControls}
              leftContent={desktopLeftContent}
              onResetZoom={handleResetZoom}
              isZoomed={isZoomed}
            />
          )}

          <div className="flex-1 min-h-0 relative flex flex-col">
            {isMobile && (
            <div className="absolute left-[3px] top-[29px] bottom-[22px] w-0.5" /> // MIKE ADD. added left 3px to shift line right on mobile and a black line
          )}
            {isMobile ? ( // show mobile time label and controls in a row on mobile, but only time label on desktop since controls are in the header
              <div className="flex items-center justify-between">
                <GraphMobileTimeLabel isMobile={isMobile} timeZone={timeZone} />
                {!hideMobileControls && mobileControls}
              </div>
            ) : (
              <GraphMobileTimeLabel isMobile={isMobile} timeZone={timeZone} />
            )}
            {/* Takes only what the phone time label leaves, so the chart can't spill past its box */}
            <div className="relative min-h-0 flex-1">
              <Line
                ref={chartRef}
                className="h-full w-full"
                options={options}
                data={boxplotData}
                plugins={[verticalLineIntersect, dismissTooltipPlugin]}
                onTouchStart={handleTouchMove}
                onTouchMove={handleTouchMove}
              />
            </div>
          </div>

          <GraphModals
            isDatasetOpen={isDatasetModalOpen}
            isTimeRangeOpen={isTimeRangeModalOpen}
            onCloseDataset={() => setIsDatasetModalOpen(false)}
            onCloseTimeRange={() => setIsTimeRangeModalOpen(false)}
            datasets={datasetItems}
            onToggleVisibility={(id) => toggleDatasetVisibility(parseInt(id))}
            timeRanges={TIME_RANGES}
            selectedTimeRange={selectedTimeRange}
            onSelectTimeRange={applyTimeRange}
            timeZone={timeZone}
            onTimeZoneSelect={(zone) => {
              if (typeof onTimeZoneChange === 'function') {
                onTimeZoneChange(zone);
              }
            }}
            assetTimeZone={assetTimeZone}
            timeZoneMode={timeZoneMode}
          />
        </div>
      );
    }
    return false;
  }
);

export default ProcessedScatterGraph;
